import React, { createContext, useContext, useState, useCallback } from 'react';
import translations from '../i18n/translations';
import { resolveSlug } from '../data/categoryIcons';

const STORAGE_KEY = 'ber_lang';
const SUPPORTED = ['tr', 'de', 'en'];

function detectBrowserLang() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED.includes(saved)) return saved;

  const nav = navigator.language || navigator.userLanguage || '';
  const code = nav.slice(0, 2).toLowerCase();
  if (SUPPORTED.includes(code)) return code;
  return 'de'; // default: German (platform is for Germany)
}

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectBrowserLang);

  const setLang = useCallback((code) => {
    if (!SUPPORTED.includes(code)) return;
    localStorage.setItem(STORAGE_KEY, code);
    setLangState(code);
  }, []);

  const t = useCallback((key) => {
    const parts = key.split('.');
    let val = translations[lang];
    for (const part of parts) {
      if (val == null) break;
      val = val[part];
    }
    if (val == null) {
      // fallback to Turkish
      let fb = translations['tr'];
      for (const part of parts) {
        if (fb == null) break;
        fb = fb[part];
      }
      return fb ?? key;
    }
    return val;
  }, [lang]);

  // Translate category label by slug
  const tCat = useCallback((slug) => {
    const resolved = resolveSlug(slug);
    return translations[lang]?.categories?.[resolved] ?? resolved;
  }, [lang]);

  // Translate condition value (backend sends Turkish)
  const tCond = useCallback((turkishValue) => {
    return translations[lang]?.conditions?.[turkishValue] ?? turkishValue;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tCat, tCond, supported: SUPPORTED }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}
